import createDomPurify from 'dompurify';

import {createView, css} from '@sail/ui';
import {
  css as deprecatedCss,
  view as deprecatedView,
} from '@sail/ui/deprecated';
import {scheduleStyleIntent} from '@sail/engine';

import {useTheme} from '@stripe-internal/conversations-ui';

import {dotComColors} from '../../theme/dotCom';
import {renderMarkdownInline} from '../../helpers/markdown';

const DOMPurify = createDomPurify();

// From https://github.com/cure53/DOMPurify/issues/317#issuecomment-728100494
// This hook sets all links to open in a new tab instead of navigating away in the same tab
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener');
  }
});

const dotComBackgroundToFontColorMapping = {
  neutral100: deprecatedCss.value(dotComColors.brand600),
  neutral600: deprecatedCss.value(dotComColors.brand),
} as const;

const className = 'uh-conversations-chat-text-body';
const chatSelector = `.${className}`;
const userMessage = `[data-message-type="user"] ${chatSelector}`;

const splashLinkStyles = css.global({
  [`${chatSelector}-splash a`]: {
    color: 'var(--link-color)',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    textDecorationThickness: '0.07em',
  },
  [`${userMessage}-splash a`]: {
    color: 'inherit',
  },
});

scheduleStyleIntent(splashLinkStyles);

export type Props = {
  body: string;
  background?: 'neutral100' | 'neutral600';
  textSize?: 'default' | 'small';
  css?: React.ComponentProps<typeof deprecatedView.div>['css'];
};

export const TextBody = createView(
  ({
    body,
    background = 'neutral100',
    textSize = 'default',
    ...props
  }: Props) => {
    const theme = useTheme();

    return (
      <deprecatedView.div
        className={`${className}-splash`}
        css={{
          stack: 'y',
          gap: 'small',
          '--link-color': deprecatedCss.value(theme.linkColor),
        }}
        {...props}
      >
        <deprecatedView.div>
          {body.split(/\r?\n/g).map((p, i) => (
            <deprecatedView.span
              // eslint-disable-next-line react/no-array-index-key
              key={`text-row-body-${i}`}
              css={{
                color: dotComBackgroundToFontColorMapping[background],
                font: textSize === 'small' ? 'caption' : 'body',
              }}
            >
              <deprecatedView.span
                // Sanitize the html here so that we prevent any security risks
                //
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(renderMarkdownInline(p)),
                }}
              />
              <br />
            </deprecatedView.span>
          ))}
        </deprecatedView.div>
      </deprecatedView.div>
    );
  },
);
